// src/components/store/sections/featured-products/DomeProductCard.tsx
"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Star } from "lucide-react";
import { useCart } from "@/context/CartContext";

export interface ProductItem {
  id: number | string;
  name: string;
  slug?: string;
  price: number | string;
  regular_price?: number | string;
  image?: string | null;
  primary_image?: string | null;
  category_name?: string;
  description?: string;
  rating?: number;
}

// 🌟 Screenshot-er 3-ti theme (Blue/Lime, Ruby Red/Peach, Sunset Orange/Citrus)
const DOME_THEMES = [
  {
    bgGradient: "from-[#38bdf8] via-[#0284c7] to-[#0369a1]",
    textColor: "#0284c7",
    buttonColor: "#0284c7",
    badgeColor: "#0369a1",
    fallbackImg: "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=500&auto=format&fit=crop&q=80",
    fruits: [
      { top: "6%", left: "6%", size: 38, rot: "-15deg", color: "#bef264" },
      { top: "42%", left: "5%", size: 42, rot: "25deg", color: "#a3e635" },
      { top: "24%", right: "6%", size: 36, rot: "-30deg", color: "#bef264" },
      { top: "40%", right: "8%", size: 40, rot: "15deg", color: "#84cc16" },
    ],
  },
  {
    bgGradient: "from-[#fb7185] via-[#e11d48] to-[#9f1239]",
    textColor: "#e11d48",
    buttonColor: "#e11d48",
    badgeColor: "#be123c",
    fallbackImg: "https://images.unsplash.com/photo-1513558161293-cdaf765ed2fd?w=500&auto=format&fit=crop&q=80",
    fruits: [
      { top: "6%", left: "18%", size: 40, rot: "35deg", color: "#fca5a5" },
      { top: "36%", left: "4%", size: 44, rot: "-20deg", color: "#f87171" },
      { top: "20%", right: "6%", size: 42, rot: "45deg", color: "#fca5a5" },
      { top: "38%", right: "12%", size: 36, rot: "-10deg", color: "#f87171" },
    ],
  },
  {
    bgGradient: "from-[#fde047] via-[#fb923c] to-[#ea580c]",
    textColor: "#ea580c",
    buttonColor: "#ea580c",
    badgeColor: "#c2410c",
    fallbackImg: "https://images.unsplash.com/photo-1621263764928-df1444c5e859?w=500&auto=format&fit=crop&q=80",
    fruits: [
      { top: "6%", left: "12%", size: 38, rot: "-25deg", color: "#fdba74" },
      { top: "42%", left: "5%", size: 40, rot: "15deg", color: "#fb923c" },
      { top: "18%", right: "8%", size: 42, rot: "30deg", color: "#fed7aa" },
      { top: "36%", right: "4%", size: 46, rot: "-15deg", color: "#f97316" },
    ],
  },
];

interface DomeProductCardProps {
  product: ProductItem;
  index?: number;
}

export default function DomeProductCard({
  product,
  index = 0,
}: DomeProductCardProps) {
  const [loading, setLoading] = useState(false);
  const cartContext = useCart?.() as any;

  const theme = DOME_THEMES[index % DOME_THEMES.length];

  const formatImageUrl = (img?: string | null) => {
    if (!img) return null;
    if (img.startsWith("http://") || img.startsWith("https://")) return img;
    return `http://127.0.0.1:8000${img.startsWith("/") ? "" : "/"}${img}`;
  };

  const productImg =
    formatImageUrl(product.primary_image || product.image) || theme.fallbackImg;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);

    if (cartContext && typeof cartContext.addToCart === "function") {
      cartContext.addToCart(product, 1);
      if (typeof cartContext.openCart === "function") cartContext.openCart();
    } else {
      window.dispatchEvent(
        new CustomEvent("addToCart", { detail: { product, quantity: 1 } })
      );
      window.dispatchEvent(new CustomEvent("openCartDrawer"));
    }

    setTimeout(() => setLoading(false), 600);
  };

  const priceNum = Number(product.price) || 0;

  return (
    <div className="relative w-full rounded-[30px] overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col justify-between group min-h-[460px] sm:min-h-[490px] select-none border border-slate-200/40">
      
      {/* 🌟 1. Background Sunburst & Gradient Layer */}
      <div className={`absolute inset-0 bg-gradient-to-b ${theme.bgGradient} overflow-hidden pointer-events-none`}>
        {/* Sunburst Rays Effect (SVG Pattern) */}
        <svg
          className="absolute inset-0 w-full h-[65%] opacity-20 mix-blend-overlay pointer-events-none"
          viewBox="0 0 400 400"
          preserveAspectRatio="none"
        >
          <g transform="translate(200, 160)">
            {Array.from({ length: 24 }).map((_, i) => (
              <polygon
                key={i}
                points="0,0 -15,-300 15,-300"
                fill="#ffffff"
                transform={`rotate(${i * 15})`}
              />
            ))}
          </g>
        </svg>

        {/* Floating Fruit Slices (Decorative Slices) */}
        {theme.fruits.map((f, i) => (
          <div
            key={i}
            className="absolute rounded-full border-2 border-white/60 shadow-xs flex items-center justify-center pointer-events-none opacity-85 transition-transform duration-700 group-hover:scale-110"
            style={{
              top: f.top,
              left: (f as any).left,
              right: (f as any).right,
              width: `${f.size}px`,
              height: `${f.size}px`,
              transform: `rotate(${f.rot})`,
              backgroundColor: f.color,
            }}
          >
            <div className="w-[80%] h-[80%] rounded-full border border-white/50 border-dashed" />
          </div>
        ))}
      </div>

      {/* 🌟 2. Top-Right Floating Round Price Badge */}
      <div 
        style={{ color: theme.badgeColor }}
        className="absolute top-3.5 right-3.5 z-20 w-9 h-9 rounded-full bg-white shadow-md flex items-center justify-center font-black text-xs font-mono tracking-tight"
      >
        ৳{Math.round(priceNum).toLocaleString()}
      </div>

      {/* 🌟 3. Product Hero Glass / Showcase Image (Upper Section) */}
      <div className="relative z-10 w-full h-56 sm:h-60 flex items-center justify-center pt-5 px-6">
        <Link href={`/products/${product.slug || product.id}`} className="relative w-40 h-40 sm:w-44 sm:h-44 group-hover:scale-105 transition-transform duration-500 drop-shadow-2xl">
          <Image
            src={productImg}
            alt={product.name}
            fill
            unoptimized
            className="object-contain"
          />
        </Link>
      </div>

      {/* 🌟 4. Bottom Arched White Dome Card Container */}
      <div className="relative z-10 w-full bg-white rounded-t-[105px] pt-8 pb-5 px-5 text-center flex flex-col items-center justify-between shadow-2xl min-h-[200px]">
        
        {/* Product Title (Colored Bold Caps) */}
        <div className="space-y-1 w-full max-w-[220px]">
          <Link href={`/products/${product.slug || product.id}`} className="block">
            <h3
              style={{ color: theme.textColor }}
              className="text-sm sm:text-base font-black tracking-wider uppercase truncate hover:underline"
              title={product.name}
            >
              {product.name || "PRODUCT NAME"}
            </h3>
          </Link>

          {/* Subtitle / Description */}
          <p className="text-[10px] text-slate-400 font-medium line-clamp-2 leading-relaxed">
            {product.description || "Lorem ipsum dolor sit amet, consectetur adipiscing elit."}
          </p>
        </div>

        {/* 5 Rating Stars */}
        <div className="flex items-center justify-center gap-1 my-2">
          {[1, 2, 3, 4].map((s) => (
            <Star key={s} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
          ))}
          <Star className="w-3.5 h-3.5 fill-slate-200 text-slate-300" />
        </div>

        {/* Pill-Shaped "ADD TO CART" Button */}
        <button
          type="button"
          onClick={handleAddToCart}
          disabled={loading}
          style={{ backgroundColor: theme.buttonColor }}
          className="w-full max-w-[170px] py-2 rounded-full text-white font-black text-[10px] sm:text-[11px] tracking-wider uppercase transition-all shadow-md hover:opacity-90 active:scale-95 cursor-pointer disabled:opacity-60"
        >
          {loading ? "ADDING..." : "ADD TO CART"}
        </button>
      </div>

    </div>
  );
}